import { BaseControl } from '.';
import calendarPeriodRange, { calendarPeriodPresets, toISODate } from '../../../../helpers/compareHelpers/calendarPeriodPresets';

// AI-CONTEXT: See docs/features/livereport-widgets-housevisits-personas.md
// DateRangeControl — picks a start + end date with preset shortcuts.
// Value shape: { start: string|null, end: string|null } where strings are
// ISO date ('YYYY-MM-DD') or null for unbounded.
//
// Presets compute concrete start/end values at click time relative to "today"
// and write them into the value — so on reload, the persisted range is exactly
// what the user saw. (Presets are UI-only; not stored.) Custom is just "edit
// the two date inputs directly".

// Day windows count back from today; calendar presets (quarters, years) cover
// the whole period — see calendarPeriodPresets.
const PRESETS = [
    { key: 'last7',  label: 'Laatste 7 dagen',  days: 7 },
    { key: 'last30', label: 'Laatste 30 dagen', days: 30 },
    { key: 'last90', label: 'Laatste 90 dagen', days: 90 },
    ...calendarPeriodPresets.map((p) => ({ ...p, calendar: true })),
    { key: 'all',    label: 'Alles',            days: null },
]

/**
 * @typedef {object} DateRangeValue
 * @property {string|null} start - ISO date string 'YYYY-MM-DD' or null (unbounded)
 * @property {string|null} end   - ISO date string 'YYYY-MM-DD' or null (unbounded)
 */

/**
 * @extends {BaseControl<DateRangeValue>}
 */
export class DateRangeControl extends BaseControl {
    /** @type {BaseControl<DateRangeValue>['render']} */
    render(value, onChange) {
        return (
            <DateRangeControlRenderer
                value={value || { start: null, end: null }}
                onChange={onChange}
                label={this.label}
                description={this.description}
            />
        )
    }
}

/**
 * Also reused by PeriodOverrideControl for its custom-range state.
 * @param {object} props
 * @param {DateRangeValue} props.value - Current {start, end} range
 * @param {(value: DateRangeValue) => void} props.onChange - Fires on preset click or manual date edit
 * @param {string} [props.label] - Optional label rendered above the inputs
 * @param {string} [props.description] - Optional description rendered below the inputs
 */
export const DateRangeControlRenderer = ({ value, onChange, label, description }) => {

    const applyPreset = (preset) => {
        if (preset.calendar) {
            onChange(calendarPeriodRange(preset.key))
            return
        }
        if (preset.days == null) {
            onChange({ start: null, end: null })
            return
        }
        const today = new Date()
        const start = new Date(today)
        start.setDate(today.getDate() - preset.days)
        onChange({ start: toISODate(start), end: toISODate(today) })
    }

    return (
        <div className="date-range-control">
            {label && <span className="date-range-control-label">{label}</span>}
            <div className="date-range-control-presets">
                {PRESETS.map(p => (
                    <button
                        key={p.key}
                        type="button"
                        className="date-range-control-preset"
                        onClick={() => applyPreset(p)}
                    >
                        {p.label}
                    </button>
                ))}
            </div>
            <div className="date-range-control-inputs">
                <label>
                    <span>Van</span>
                    <input
                        type="date"
                        value={value.start || ''}
                        onChange={(e) => onChange({ ...value, start: e.target.value || null })}
                    />
                </label>
                <label>
                    <span>Tot</span>
                    <input
                        type="date"
                        value={value.end || ''}
                        onChange={(e) => onChange({ ...value, end: e.target.value || null })}
                    />
                </label>
            </div>
            {description && <p className="date-range-control-description">{description}</p>}
        </div>
    )
}
